import { TonClient } from "ton";
import { getHttpEndpoint } from "@orbs-network/ton-access";
import { getNetwork } from "./hooks/useNetwork";
import { TonRpcClient } from "./ton-rpc-client";

type Network = ReturnType<typeof getNetwork>;

const DISCOVERY_TIMEOUT_MS = 5000;
const fallbackEndpoints: Record<Network, string> = {
  mainnet: "https://toncenter.com/api/v2/jsonRPC",
  testnet: "https://testnet.toncenter.com/api/v2/jsonRPC",
};
const endpoints = new Map<Network, Promise<string>>();
const clients = new Map<Network, TonClient>();

async function discoverEndpoint(network: Network): Promise<string> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const endpoint = await Promise.race([
      getHttpEndpoint({ network }),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(
          () => reject(new Error("TON endpoint discovery timed out")),
          DISCOVERY_TIMEOUT_MS,
        );
      }),
    ]);
    if (!endpoint?.trim()) {
      throw new Error("TON endpoint discovery returned an empty endpoint");
    }
    return endpoint;
  } catch (error) {
    console.warn("TON endpoint discovery failed; using fallback RPC", error);
    return fallbackEndpoints[network];
  } finally {
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }
  }
}

function getEndpointForNetwork(network: Network): Promise<string> {
  let endpoint = endpoints.get(network);
  if (!endpoint) {
    endpoint = discoverEndpoint(network);
    endpoints.set(network, endpoint);
  }
  return endpoint;
}

export async function getClient() {
  const network = getNetwork(new URLSearchParams(window.location.search));
  const endpoint = await getEndpointForNetwork(network);
  let client = clients.get(network);
  if (!client) {
    client = new TonRpcClient({ endpoint }, endpoint === fallbackEndpoints[network] ? 1100 : 0);
    clients.set(network, client);
  }
  return client;
}

export async function getEndpoint() {
  return getEndpointForNetwork(getNetwork(new URLSearchParams(window.location.search)));
}
